import { OrderTemplate } from "../../models";
import { Timestamp } from "../../types";

/** Minimal translate signature so these helpers stay framework-agnostic. */
export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/** Weekday indices used across the app: 0 = Monday ... 6 = Sunday. */
export const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/** Comma-separated localized weekday short names (Mon, Wed, …). */
export const formatWeekdays = (t: TranslateFn, days?: number[]): string =>
  (days ?? [])
    .slice()
    .sort((a, b) => a - b)
    .map((d) => t(`templates.recurrence.weekday_short.${WEEKDAY_KEYS[d]}`))
    .join(", ");

/** Human-readable "how often" summary (frequency + interval + days). */
export const describeSchedule = (
  t: TranslateFn,
  template: OrderTemplate,
): string => {
  const rule = template.recurrence;
  if (!rule) return "";
  if (rule.frequency === "weekly") {
    return t("templates.summary.weekly", {
      count: rule.interval,
      days: formatWeekdays(t, rule.byWeekday),
    });
  }
  if (rule.frequency === "monthly") {
    return t("templates.summary.monthly", {
      count: rule.interval,
      day: rule.byMonthDay ?? 1,
    });
  }
  return t("templates.summary.daily", { count: rule.interval });
};

/** Human-readable end condition (never / on date / after N orders). */
export const describeEnd = (t: TranslateFn, template: OrderTemplate): string => {
  const rule = template.recurrence;
  if (!rule) return "";
  if (rule.endMode === "onDate") {
    return t("templates.summary.ends_on", {
      date: Timestamp.toDisplayDate(rule.endDate),
    });
  }
  if (rule.endMode === "afterCount") {
    return t("templates.summary.ends_after", { count: rule.count ?? 0 });
  }
  return t("templates.summary.ends_never");
};
